import React, {
	useEffect,
	useState,
	useContext,
	useRef,
	useCallback,
} from "react";
import { Card, Spinner, Form, Button } from "react-bootstrap";
import VideoModal from "../../workspace/components/VideoModal";
import {
	FaChevronLeft,
	FaChevronRight,
	FaSearch,
	FaDownload,
} from "react-icons/fa";
import { useHttpClient } from "../../shared/hooks/http-hook";
import { AuthContext } from "../../shared/context/auth-context";
import ToastError from "../../shared/elements/ToastError";
import EmptyStateComponent from "./EmptyStateComponent";
import styles from "./ViewContentSection.module.css";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import ShareButton from "../../workspace/components/ShareButton";

const ViewContentSection = ({ videoData }) => {
	const { isLoading, error, sendRequest, clearError } = useHttpClient();
	const authContext = useContext(AuthContext);
	const [videos, setVideos] = useState([]);
	const [filteredVideos, setFilteredVideos] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [isSearching, setIsSearching] = useState(false);
	const [isSearchExpanded, setIsSearchExpanded] = useState(false);
	const [randomVideos, setRandomVideos] = useState([]);
	const [searchResultsPage, setSearchResultsPage] = useState(1);
	const searchResultsPerPage = 7; // Initial number of results to show
	const searchContainerRef = useRef(null);
	// Start from index 3 to skip the 3 most recent videos shown in RecentVideosSection
	const [currentOffset, setCurrentOffset] = useState(3);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [selectedVideo, setSelectedVideo] = useState(null);
	const videosPerPage = 8;

	// Function to get random videos for initial search expansion
	const getRandomVideos = (allVideos, count = 7) => {
		// Ensure allVideos is an array
		if (!Array.isArray(allVideos)) return [];

		if (allVideos.length <= count) return [...allVideos];

		const shuffled = [...allVideos].sort(() => 0.5 - Math.random());
		return shuffled.slice(0, count);
	};

	useEffect(() => {
		setVideos(videoData);
		setFilteredVideos(videoData);
		setRandomVideos(getRandomVideos(videoData));
	}, [videoData]);

	// Handle clicks outside the search container to close it
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				searchContainerRef.current &&
				!searchContainerRef.current.contains(event.target)
			) {
				setIsSearchExpanded(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// Filter videos based on search term for the search dropdown
	useEffect(() => {
		if (searchTerm.trim() === "") {
			return;
		}

		setIsSearching(true);
		const timer = setTimeout(() => {
			const filtered = videos.filter((video) =>
				video.title.toLowerCase().includes(searchTerm.toLowerCase())
			);
			setFilteredVideos(filtered);
			setIsSearching(false);

			// Only update the search results, not the main grid
			if (isSearchExpanded) {
				// Reset to page 1 when search term changes
				setSearchResultsPage(1);
				setRandomVideos(filtered.slice(0, searchResultsPerPage));
			}
		}, 300);

		return () => clearTimeout(timer);
	}, [searchTerm, videos, isSearchExpanded]);

	// Handle loading more search results
	const handleLoadMoreSearchResults = () => {
		const nextPage = searchResultsPage + 1;
		const startIndex = (nextPage - 1) * searchResultsPerPage;
		const endIndex = startIndex + searchResultsPerPage;

		// Get the next batch of videos
		const nextBatch = filteredVideos.slice(startIndex, endIndex);

		if (nextBatch.length > 0) {
			// Add the next batch to the current results
			setRandomVideos([...randomVideos, ...nextBatch]);
			setSearchResultsPage(nextPage);
		}
	};

	const handleSearchFocus = () => {
		setIsSearchExpanded(true);
	};

	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value);
	};

	const handleVideoClick = async (video) => {
		try {
			const response = await sendRequest(
				`/api/videos/${video.id}/details`,
				"GET",
				null,
				{
					Authorization: "Bearer " + authContext.token,
				}
			);
			setSelectedVideo(response.data);
			setShowModal(true);
		} catch (err) {
			console.error("Error fetching video details:", err);
		}
	};

	const handleCloseModal = () => {
		setShowModal(false);
	};

	const handleDeleteVideo = async () => {
		if (!selectedVideo) return;

		try {
			await sendRequest(`/api/videos/${selectedVideo.id}`, "DELETE", null, {
				Authorization: "Bearer " + authContext.token,
			});

			// Remove the deleted video from the list
			setVideos((prevVideos) =>
				prevVideos.filter((video) => video.id !== selectedVideo.id)
			);

			// Close the modal
			setShowModal(false);
		} catch (err) {
			console.error("Error deleting video:", err);
		}
	};

	const renderStatus = (status, videoUrl, publicShared) => {
		let statusElement;

		if (status === "pending") {
			statusElement = (
				<Spinner animation="border" size="sm" variant="warning" />
			);
		} else if (status === "processing") {
			statusElement = (
				<Spinner animation="border" size="sm" variant="success" />
			);
		} else if (status === "completed") {
			statusElement = (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						width: "100%",
					}}
				>
					<OverlayTrigger
						placement="top"
						delay={{ show: 250, hide: 400 }}
						overlay={
							<Tooltip>{status === "completed" ? "Download" : status}</Tooltip>
						}
					>
						<Button
							variant="link"
							style={{ padding: "5px", borderRadius: "50%" }}
							href={videoUrl}
							target="_blank"
							rel="noopener noreferrer"
							download
						>
							<FaDownload />
						</Button>
					</OverlayTrigger>
					{publicShared && (
						<ShareButton
							videoUrl={`${window.location.origin}/video?id=${selectedVideo.id}`}
							title={selectedVideo.title}
						/>
					)}
				</div>
			);
		} else if (status === "failed") {
			statusElement = (
				<span
					style={{
						display: "inline-block",
						width: "10px",
						height: "10px",
						backgroundColor: "red",
						borderRadius: "50%",
					}}
				></span>
			);
		}

		return <div style={{ display: "inline-block" }}>{statusElement}</div>;
	};

	// Create a reusable transition function using useCallback
	const transitionCards = useCallback((newOffset) => {
		// Set transitioning state to show placeholders
		setIsTransitioning(true);

		// Immediately update to new offset
		setCurrentOffset(newOffset);

		// Very short timeout just to ensure DOM updates
		setTimeout(() => {
			setIsTransitioning(false);
		}, 10); // Minimal delay, just enough for render cycle
	}, []);

	const handlePrevPage = () => {
		if (isTransitioning) return; // Prevent multiple clicks during transition
		const newOffset = Math.max(0, currentOffset - videosPerPage);
		transitionCards(newOffset);
	};

	const handleNextPage = () => {
		if (isTransitioning) return; // Prevent multiple clicks during transition
		if (currentOffset + videosPerPage < videos.length) {
			transitionCards(currentOffset + videosPerPage);
		}
	};

	// Format date for display
	const formatDate = (createdAt) => {
		if (!createdAt) return "";
		try {
			// Handle Firestore timestamp format
			if (createdAt._seconds) {
				// Convert Firestore timestamp to JS Date
				const date = new Date(
					createdAt._seconds * 1000 + (createdAt._nanoseconds || 0) / 1000000
				);
				return date.toLocaleDateString("en-US", {
					month: "numeric",
					day: "numeric",
					year: "numeric",
				});
			}
			// Handle regular date string
			else if (typeof createdAt === "string") {
				const date = new Date(createdAt);
				if (isNaN(date.getTime())) return "";
				return date.toLocaleDateString("en-US", {
					month: "numeric",
					day: "numeric",
					year: "numeric",
				});
			}
			// Handle Date object
			else if (createdAt instanceof Date) {
				return createdAt.toLocaleDateString("en-US", {
					month: "numeric",
					day: "numeric",
					year: "numeric",
				});
			}

			return "";
		} catch (error) {
			console.error("Error formatting date:", error, "for value:", createdAt);
			return "";
		}
	};

	// Sort videos by creation date (newest first)
	const sortedVideos = (Array.isArray(videos) ? [...videos] : []).sort(
		(a, b) => {
			const dateA = new Date(a.createdAt || 0);
			const dateB = new Date(b.createdAt || 0);
			return dateB - dateA;
		}
	);

	// Get the current page of videos for the main grid
	const currentVideos = sortedVideos.slice(
		currentOffset,
		currentOffset + videosPerPage
	);

	// Determine if we should disable the navigation buttons
	const isFirstPage = currentOffset === 0;
	const isLastPage = currentOffset + videosPerPage >= videos.length;

	return (
		<div className={styles.viewContentSection}>
			<ToastError show={!!error} message={error} onClose={clearError} />
			<h3 className={styles.sectionHeader}>View Content</h3>

			<div
				ref={searchContainerRef}
				className={`${styles.searchContainer} ${styles.fixedSearch} ${
					isSearchExpanded ? styles.searchExpanded : ""
				}`}
			>
				<div className={styles.searchInputWrapper}>
					<FaSearch className={styles.searchIcon} />
					<Form.Control
						type="text"
						placeholder="Search videos by title..."
						value={searchTerm}
						onChange={handleSearchChange}
						onFocus={handleSearchFocus}
						className={styles.searchInput}
					/>
				</div>

				{isSearchExpanded && (
					<div className={styles.searchResultsContainer}>
						<div className={styles.searchHeader}>
							<div className={styles.searchCategory}>
								Videos{" "}
								<span className={styles.videoCount}>{videos.length}</span>
							</div>
						</div>

						{isSearching ? (
							<div className="text-center py-3">
								<Spinner animation="border" size="sm" role="status" />
							</div>
						) : (
							<>
								<div className={styles.searchResultsList}>
									{randomVideos.map((video) => (
										<div
											key={video.id}
											className={styles.searchResultItem}
											onClick={() => handleVideoClick(video)}
											style={{ cursor: "pointer" }}
										>
											<div className={styles.searchResultThumbnail}>
												<img
													src={
														video.thumbnail_url || "/placeholder-thumbnail.jpg"
													}
													alt={video.title}
													onLoad={(e) => {
														// Detect if image is horizontal or vertical
														const img = e.target;
														if (img.naturalWidth > img.naturalHeight) {
															img.className =
																styles.searchResultThumbnailHorizontal;
														} else {
															img.className =
																styles.searchResultThumbnailVertical;
														}
													}}
												/>
											</div>
											<div className={styles.searchResultContent}>
												<div className={styles.searchResultTitle}>
													{video.title}
												</div>
												<div className={styles.searchResultDate}>
													Generated: {formatDate(video.createdAt)}
												</div>
											</div>
										</div>
									))}
								</div>

								<div className={styles.searchViewMore}>
									<Button
										className={styles.viewMoreSearchButton}
										onClick={handleLoadMoreSearchResults}
										disabled={randomVideos.length >= filteredVideos.length}
									>
										View More
									</Button>
								</div>
							</>
						)}
					</div>
				)}
			</div>

			{(isLoading && videos.length === 0) ? (
				<div className="text-center py-4">
					<Spinner animation="border" role="status" />
				</div>
			) : videos?.length === 0 ? (
				<EmptyStateComponent type="content" />
			) : (
				<div
					className={`${styles.viewContentGridContainer} ${
						isSearchExpanded ? styles.pushedDown : ""
					}`}
				>
					<div className={styles.contentNavigation}>
						<button
							className={`${styles.contentNavArrow} ${
								isFirstPage ? "disabled" : ""
							}`}
							onClick={handlePrevPage}
							disabled={isFirstPage}
							aria-label="Previous page"
						>
							<FaChevronLeft />
						</button>
						<button
							className={`${styles.contentNavArrow} ${
								isLastPage ? "disabled" : ""
							}`}
							onClick={handleNextPage}
							disabled={isLastPage}
							aria-label="Next page"
						>
							<FaChevronRight />
						</button>
					</div>
					<div className={styles.gridContainer}>
						{/* Gray placeholder cards */}
						<div className={styles.placeholderCardsGrid}>
							{Array(videosPerPage)
								.fill()
								.map((_, index) => (
									<div
										key={`placeholder-${index}`}
										className={styles.placeholderCard}
									></div>
								))}
						</div>

						{/* Actual video cards */}
						<div
							className={`${styles.videoCardsGrid} ${
								isTransitioning ? styles.transitioning : ""
							}`}
						>
							{currentVideos.map((video) => (
								<Card
									key={video.id}
									className={`${styles.contentVideoCard}`}
									onClick={() => handleVideoClick(video)}
									style={{ cursor: "pointer" }}
								>
									<div className={styles.videoCardImageContainer}>
										<div
											className={styles.videoCardImageContainerBg}
											style={{
												backgroundImage: `url(${
													video.thumbnail_url || "/placeholder-thumbnail.jpg"
												})`,
												position: "absolute",
												top: 0,
												left: 0,
												right: 0,
												bottom: 0,
												backgroundSize: "120%",
												backgroundPosition: "center",
												filter: "blur(12px) brightness(0.5)",
												zIndex: 0,
											}}
										></div>
										<Card.Img
											variant="top"
											src={video.thumbnail_url || "/placeholder-thumbnail.jpg"}
											alt={video.title}
											className={
												video.orientation === "vertical"
													? styles.videoCardImageVertical
													: styles.videoCardImageHorizontal
											}
											onLoad={(e) => {
												// Detect if image is horizontal or vertical
												const img = e.target;
												if (img.naturalWidth > img.naturalHeight) {
													// Horizontal image
													img.className = styles.videoCardImageHorizontal;
												} else {
													// Vertical image
													img.className = styles.videoCardImageVertical;
												}
											}}
										/>
									</div>
									<div className={styles.videoCardOverlay}>
										<div className={styles.videoCardOverlayTitle}>
											{video.title}
										</div>
									</div>
								</Card>
							))}
						</div>
					</div>
				</div>
			)}

			{/* Video Modal for displaying video details */}
			{selectedVideo && (
				<VideoModal
					show={showModal}
					onHide={handleCloseModal}
					video={selectedVideo}
					onDelete={handleDeleteVideo}
					renderStatus={renderStatus}
					isLoading={false}
				/>
			)}
		</div>
	);
};

export default ViewContentSection;
